import React from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const ValidationResult = ({ result, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg p-6 border border-border">
        <div className="text-center py-12">
          <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Validating QR code...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="bg-card rounded-lg p-6 border border-border">
        <div className="text-center py-12">
          <Icon name="QrCode" size={64} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-bold mb-2">Validation Result</h3>
          <p className="text-muted-foreground">
            Scan a QR code to see validation results here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <h2 className="text-xl font-bold mb-6">Validation Result</h2>

      {/* Status Banner */}
      <div className={`p-6 rounded-lg mb-6 ${
        result?.success
          ? 'bg-success/10 border border-success' :'bg-destructive/10 border border-destructive'
      }`}>
        <div className="flex items-center gap-3 mb-2">
          <Icon
            name={result?.success ? 'CheckCircle' : 'XCircle'}
            size={32}
            className={result?.success ? 'text-success' : 'text-destructive'}
          />
          <div>
            <h3 className={`text-xl font-bold ${
              result?.success ? 'text-success' : 'text-destructive'
            }`}>
              {result?.success ? 'Valid QR Code' : 'Invalid QR Code'}
            </h3>
            <p className={`text-sm ${
              result?.success ? 'text-success' : 'text-destructive'
            }`}>
              {result?.message}
            </p>
          </div>
        </div>
      </div>

      {/* Member Information */}
      {result?.member && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
            {result?.member?.avatar_url ? (
              <AppImage
                src={result?.member?.avatar_url}
                alt={result?.member?.full_name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="User" size={32} className="text-primary" />
              </div>
            )}
            <div className="flex-1">
              <h4 className="font-bold text-lg">{result?.member?.full_name}</h4>
              <p className="text-sm text-muted-foreground">{result?.member?.email}</p>
              {result?.member?.phone && (
                <p className="text-sm text-muted-foreground">{result?.member?.phone}</p>
              )}
            </div>
          </div>

          {/* Membership Details */}
          {result?.membershipPlan && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Membership Plan</p>
              <p className="font-semibold capitalize">{result?.membershipPlan?.replace('_', ' ')}</p>
            </div>
          )}

          {/* Check-in/Check-out Info */}
          {result?.type === 'check_in' && (
            <div className="p-4 bg-success/10 border border-success rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="LogIn" size={20} className="text-success" />
                <p className="font-semibold text-success">Checked In</p>
              </div>
              <p className="text-sm text-muted-foreground">
                {new Date()?.toLocaleString()}
              </p>
            </div>
          )}

          {result?.type === 'check_out' && (
            <div className="p-4 bg-info/10 border border-info rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="LogOut" size={20} className="text-info" />
                <p className="font-semibold text-info">Checked Out</p>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Check-in: {new Date(result?.checkInTime)?.toLocaleString()}</p>
                <p>Check-out: {new Date()?.toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Expiration Warning */}
          {result?.expiresAt && (
            <div className="p-4 bg-warning/10 border border-warning rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={20} className="text-warning" />
                <p className="font-semibold text-warning">Expired QR Code</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Expired on: {new Date(result?.expiresAt)?.toLocaleString()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error Types */}
      {!result?.success && !result?.member && (
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2">Error Details</h4>
          <p className="text-sm text-muted-foreground">
            {result?.type === 'not_found' && 'The scanned QR code is not registered in the system.'}
            {result?.type === 'error' && 'An unexpected error occurred. Please try again or contact support.'}
          </p>
        </div>
      )}

      {/* Action Recommendations */}
      {!result?.success && (
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <Icon name="Info" size={18} />
            Recommended Actions
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
            {result?.type === 'not_found' && (
              <>
                <li>Verify the QR code is from this gym</li>
                <li>Check if the member has generated a valid QR code</li>
                <li>Use manual entry if the QR code is damaged</li>
              </>
            )}
            {result?.type === 'inactive' && (
              <>
                <li>Ask the member to generate a new QR code</li>
                <li>Check member account status in the system</li>
              </>
            )}
            {result?.type === 'expired' && (
              <>
                <li>Request the member to generate a new QR code</li>
                <li>One-time QR codes expire after their validity period</li>
              </>
            )}
            {result?.type === 'member_inactive' && (
              <>
                <li>Contact reception to reactivate membership</li>
                <li>Check payment status and membership plan</li>
                <li>Verify member account details</li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ValidationResult;